import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  LogOut,
  Search,
  ShieldCheck,
  UserRoundPlus,
} from "lucide-react";
import { Link, Navigate } from "@tanstack/react-router";
import {
  accessRepository,
  groupManagedCustomers,
  normalizeAccessEmail,
  type AccessWorkspace,
} from "../platform/accessRepository";
import { usePortal } from "../state/PortalContext";

const emptyWorkspace: AccessWorkspace = {
  products: [],
  memberships: [],
  entitlements: [],
};

function offerShortLabel(label: string) {
  return label.replace(" Â· ", " · ");
}

export function AdminAccessPage() {
  const { authStatus, session, signOut } = usePortal();
  const [workspace, setWorkspace] = useState<AccessWorkspace>(emptyWorkspace);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [notice, setNotice] = useState("");

  const sessionEmail = session?.email ?? "";
  const loadWorkspace = useCallback(async () => {
    if (!sessionEmail || !accessRepository.configured) {
      setWorkspace(emptyWorkspace);
      setLoading(false);
      return;
    }
    setLoadError("");
    const nextWorkspace = await accessRepository.loadWorkspace(sessionEmail);
    setWorkspace(nextWorkspace);
    setLoading(false);
  }, [sessionEmail]);

  useEffect(() => {
    document.title = "Access management";
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex,nofollow";
  }, []);

  useEffect(() => {
    if (authStatus !== "ready" || !sessionEmail) return;
    let active = true;
    setLoading(true);
    void loadWorkspace().catch((error: unknown) => {
      if (!active) return;
      setLoading(false);
      setLoadError(
        error instanceof Error
          ? error.message
          : "Product access could not be loaded.",
      );
    });
    return () => {
      active = false;
    };
  }, [authStatus, loadWorkspace, sessionEmail]);

  const customers = useMemo(
    () => groupManagedCustomers(workspace.memberships, workspace.entitlements),
    [workspace.entitlements, workspace.memberships],
  );
  const selectedCustomer = customers.find(
    (customer) => customer.email === selectedEmail,
  );
  const activeEntitlements = new Set(
    (selectedCustomer?.entitlements ?? [])
      .filter((entitlement) => entitlement.active)
      .map(
        (entitlement) =>
          `${entitlement.productKey}:${entitlement.offerSku}`,
      ),
  );

  if (authStatus === "loading") {
    return <main className="account-boot" aria-label="Opening access management" />;
  }
  if (authStatus === "signed-out" || !session) {
    return <Navigate to="/login" replace />;
  }

  const selectEmail = (email: string) => {
    const normalizedEmail = normalizeAccessEmail(email);
    setEmailQuery(normalizedEmail);
    setSelectedEmail(normalizedEmail);
    setNotice("");
    setLoadError("");
  };

  const submitLookup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    selectEmail(emailQuery);
  };

  const toggleOffer = async (
    productKey: string,
    offerSku: string,
    enabled: boolean,
    label: string,
  ) => {
    if (!selectedEmail || savingKey) return;
    const operationKey = `${productKey}:${offerSku}`;
    setSavingKey(operationKey);
    setNotice("");
    setLoadError("");
    try {
      if (enabled) {
        await accessRepository.grantOffer(productKey, selectedEmail, offerSku);
      } else {
        await accessRepository.revokeOffer(productKey, selectedEmail, offerSku);
      }
      await loadWorkspace();
      setNotice(
        `${offerShortLabel(label)} was ${enabled ? "granted to" : "removed from"} ${selectedEmail}.`,
      );
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : "The access change could not be saved.",
      );
    } finally {
      setSavingKey("");
    }
  };

  const administratorApproved = workspace.products.length > 0;

  return (
    <main className="access-admin-page">
      <header className="access-admin-header">
        <div className="access-admin-header__inner">
          <div className="access-admin-brand">
            <span><ShieldCheck size={18} /></span>
            <div>
              <strong>Access management</strong>
              <small>AIAgentOffer administration</small>
            </div>
          </div>
          <div className="access-admin-header__actions">
            <Link to="/account"><ArrowLeft size={15} /> Back to products</Link>
            <button type="button" onClick={() => void signOut()}>
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="access-admin-shell">
        {loadError ? <p className="access-admin-alert" role="alert">{loadError}</p> : null}

        {loading ? (
          <section className="access-admin-loading" aria-busy="true">
            Loading secure access records…
          </section>
        ) : !accessRepository.configured ? (
          <section className="access-admin-empty">
            <ShieldCheck size={24} />
            <h2>Cloud access management is unavailable</h2>
            <p>This dashboard requires the configured Supabase workspace.</p>
          </section>
        ) : !administratorApproved ? (
          <section className="access-admin-empty">
            <ShieldCheck size={24} />
            <h2>Administrator access required</h2>
            <p>This account is not an active administrator for any product.</p>
            <Link to="/account">Return to products</Link>
          </section>
        ) : (
          <div className="access-admin-grid">
            <form className="access-admin-search" onSubmit={submitLookup}>
              <label className="visually-hidden" htmlFor="access-email">Customer email address</label>
              <div>
                <Search size={16} aria-hidden="true" />
                <input
                  id="access-email"
                  type="email"
                  required
                  autoComplete="off"
                  placeholder="customer@example.com"
                  value={emailQuery}
                  onChange={(event) => setEmailQuery(event.target.value)}
                />
                <button type="submit">Check access</button>
              </div>
            </form>

            {selectedEmail ? (
              <section className="access-admin-editor" aria-labelledby="access-editor-title">
                <header>
                  <div>
                    <span><UserRoundPlus size={15} /> Customer access</span>
                    <h2 id="access-editor-title">{selectedEmail}</h2>
                  </div>
                  <small>
                    {activeEntitlements.size
                      ? `${activeEntitlements.size} active offer${activeEntitlements.size === 1 ? "" : "s"}`
                      : "No access yet"}
                  </small>
                </header>
                  {notice ? <p className="access-admin-notice" role="status"><Check size={15} /> {notice}</p> : null}
                  <div className="access-product-list">
                    {workspace.products.map((product) => {
                      const membership = selectedCustomer?.memberships.find(
                        (item) => item.productKey === product.productKey,
                      );
                      const enabledCount = product.offers.filter((offer) =>
                        activeEntitlements.has(`${product.productKey}:${offer.offerSku}`),
                      ).length;
                      return (
                        <article key={product.productKey}>
                          <header>
                            <div>
                              <h3>{product.displayName}</h3>
                              <p>{enabledCount} of {product.offers.length} offers enabled</p>
                            </div>
                            <span className={membership?.status === "active" ? "is-active" : ""}>
                              {membership?.role === "admin"
                                ? "Administrator"
                                : membership?.status === "active"
                                  ? "Customer"
                                  : "No membership"}
                            </span>
                          </header>
                          <div className="access-offer-list">
                            {product.offers.map((offer) => {
                              const key = `${product.productKey}:${offer.offerSku}`;
                              const enabled = activeEntitlements.has(key);
                              const saving = savingKey === key;
                              return (
                                <div key={offer.offerSku}>
                                  <span>
                                    <strong>{offerShortLabel(offer.label)}</strong>
                                    <small>
                                      {offer.offerSku === product.coreOfferSku
                                        ? "Main product access"
                                        : "Additional offer access"}
                                    </small>
                                  </span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={enabled}
                                    aria-label={`${enabled ? "Remove" : "Grant"} ${offerShortLabel(offer.label)} for ${selectedEmail}`}
                                    className={`access-offer-switch ${enabled ? "is-on" : ""}`}
                                    disabled={Boolean(savingKey)}
                                    onClick={() => void toggleOffer(
                                      product.productKey,
                                      offer.offerSku,
                                      !enabled,
                                      offer.label,
                                    )}
                                  >
                                    <span />
                                    {saving ? "Saving…" : enabled ? "Active" : "Off"}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </article>
                      );
                    })}
                  </div>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </main>
  );
}
